import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

const storeTypes = ['MAIN', 'COLD', 'BAR', 'BUTCHERY', 'BAKERY', 'OTHER'];

function FoodStoreMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editingId, setEditingId] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const [form, setForm] = useState({
    store_name: '',
    store_type: 'MAIN',
    is_active: true
  });

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  const parseResponse = async (res) => {
    const raw = await res.text();
    try {
      const json = raw ? JSON.parse(raw) : {};
      return { data: json, message: json?.error || '' };
    } catch {
      return { data: {}, message: raw || '' };
    }
  };

  const resetForm = () => {
    setEditingId('');
    setForm({ store_name: '', store_type: 'MAIN', is_active: true });
  };

  const loadRows = async () => {
    if (!chainId || !propertyId) return setRows([]);
    try {
      const res = await fetch('/api/v1/food/masters/stores?all=1', { headers: headers() });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Failed to load stores');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading stores');
    }
  };

  useEffect(() => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      setRows([]);
      return;
    }
    setError('');
    setMessage('');
    loadRows();
  }, [token, chainId, propertyId]);

  const save = async () => {
    setError('');
    setMessage('');
    if (!chainId || !propertyId) return setError('Select a property first from Hotel Config');
    if (!String(form.store_name || '').trim()) return setError('Store name is required');
    try {
      const res = await fetch(editingId ? `/api/v1/food/masters/stores/${editingId}` : '/api/v1/food/masters/stores', {
        method: editingId ? 'PUT' : 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          store_name: form.store_name,
          store_type: form.store_type,
          is_active: form.is_active !== false
        })
      });
      const { message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Save failed');
      setMessage(editingId ? 'Store updated' : 'Store created');
      resetForm();
      loadRows();
    } catch {
      setError('Network error while saving store');
    }
  };

  const editRow = (row) => {
    setEditingId(row.id);
    setForm({
      store_name: row.store_name || '',
      store_type: row.store_type || 'MAIN',
      is_active: row.is_active !== false
    });
    scrollToForm();
  };

  const deactivate = async (id) => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/food/masters/stores/${id}`, { method: 'DELETE', headers: headers() });
      const { message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Deactivate failed');
      setMessage('Store deactivated');
      loadRows();
    } catch {
      setError('Network error while deactivating store');
    }
  };

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px' };
  const btnBase = { padding: '0.5rem 0.85rem', border: 'none', borderRadius: '6px', cursor: 'pointer' };

  return (
    <div className="page-shell">
      <h2 style={{ color: '#2f855a' }}>Food Management - Store Master</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar" ref={formRef} style={editHighlightStyle(editHighlight)}>
        <h3>{editingId ? 'Edit Store' : 'Add Store'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem' }}>
          <input
            value={form.store_name}
            onChange={(e) => setForm({ ...form, store_name: e.target.value })}
            style={inputStyle}
            placeholder="Store Name"
          />
          <select value={form.store_type} onChange={(e) => setForm({ ...form, store_type: e.target.value })} style={inputStyle}>
            {storeTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </label>
        </div>
        <div style={{ marginTop: '0.7rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={save} className="btn-success" style={btnBase}>{editingId ? 'Update' : 'Add'}</button>
          <button onClick={resetForm} className="btn-muted" style={btnBase}>Reset</button>
        </div>
      </div>

      <div className="filter-bar">
        <h3>Store List</h3>
        {rows.length === 0 ? (
          <p>No stores found</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#2f855a', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Store Name</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Store Type</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Active</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.6rem' }}>{r.store_name}</td>
                  <td style={{ padding: '0.6rem' }}>{r.store_type || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.is_active ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '0.6rem', display: 'flex', gap: '0.4rem' }}>
                    <button onClick={() => editRow(r)} className="btn-primary" style={btnBase}>Edit</button>
                    {r.is_active && <button onClick={() => deactivate(r.id)} className="btn-danger" style={btnBase}>Deactivate</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default FoodStoreMaster;
